<template>
  <div>

    <div v-if="!show404 && !show403">
      <VendorHero v-if="thirdParty.id"
                  id="update_basic_info"
                  :vendor="thirdParty"
                  :contracts="tableItems"
                  :contractCount="totalRows"
                  @edit="$refs.editPanel.show()"
                  @logo="$refs.logoPanel.show()"
                  @delete="showConfirmDeleteModal(thirdParty)"
      ></VendorHero>
      <!-- The hero's outline while the third party loads, so the tabs don't move down afterwards (US 9371) -->
      <div v-else class="sx-hero is-loading" aria-hidden="true" qid="vendor-hero-loading">
        <span class="sx-hero__logo"></span>
        <div class="sx-hero__main">
          <span class="sx-skel is-short"></span>
          <span class="sx-skel is-title"></span>
          <span class="sx-skel is-long"></span>
          <span class="sx-skel is-tags"></span>
        </div>
        <div class="sx-hero__people">
          <span class="sx-skel is-short"></span>
          <span class="sx-skel is-avatar"></span>
          <span class="sx-skel is-short"></span>
        </div>
        <div class="sx-hero__done">
          <span class="sx-skel is-ring"></span>
          <span class="sx-skel is-short"></span>
        </div>
        <div class="sx-hero__act">
          <span class="sx-skel is-button"></span>
        </div>
      </div>
      <!-- The third party first, then its contracts (US 9369 + 9371). Picking one switches this page. -->
      <VendorSwitcher ref="switcher"
                      :vendor="$route.params.slug"
                      controlled
                      :picked="picked ? picked.id : 'parent'"
                      :can-add="canAddContract"
                      @loaded="rowLoaded"
                      @select="pick"
                      @changed="sharedDecided"
                      @add="addContract"></VendorSwitcher>
      <div v-if="getGuide('update_basic_info')" class="mt-2">
        <GuideButton :guide="getGuide('update_basic_info')"></GuideButton>
      </div>

      <!-- Logo: upload, or let the AI find it on the company's website (US 9371) -->
      <!-- Edit details: every field of the third party in one side panel (US 9371) -->
      <VendorEditPanel v-if="thirdParty.id"
                       ref="editPanel"
                       :vendor="thirdParty"
                       @saved="detailsSaved"
                       @logo="logoSaved"></VendorEditPanel>

      <V2SidePanel v-if="thirdParty.id"
                   ref="logoPanel"
                   :kind="$t('vendor_hero.kind')"
                   :title="$t('vendor_hero.logo_title')"
                   :intro="$t('vendor_hero.logo_intro', { name: thirdParty.name })"
                   qid="vendor-logo-panel">
        <V2LogoPicker :endpoint="'vendors/' + thirdParty.slug + '/logo'"
                      :value="thirdParty.logo_large || thirdParty.logo"
                      :name="thirdParty.name"
                      qid="vendor-logo"
                      @saved="logoSaved"
        ></V2LogoPicker>
      </V2SidePanel>

      <!-- A contract picked in the row: its header, tabs and cards, with the third party on the right (US 9369 + 9371) -->
      <div v-if="picked" class="pp-pane is-kid" qid="vendor-contract-pane">
        <ThirdPartiesView :key="picked.slug"
                          embedded
                          :slug="picked.slug"
                          :index="pickedIndex"
                          :count="ownContracts.length"
                          :vendor="thirdParty.id ? thirdParty : null"
                          :vendor-summary="summary"
                          @changed="contractChanged"
                          @moved="contractMoved"
                          @deleted="contractDeleted"
                          @edit-vendor="$refs.editPanel.show()"
                          @open-vendor="pick('parent')"></ThirdPartiesView>
      </div>

      <!-- The third party picked: its own tabs. Kept while a contract is picked, so its summary stays loaded. -->
      <div v-show="ready && !picked" class="pp-pane is-par" qid="vendor-pane">
      <!-- Our own tab bar, as on the system and contract pages (US 9370, US 9371) -->
      <nav class="sx-tabs mt-3" role="tablist" qid="vendor-tabs">
        <button v-for="item in tabItems"
                :key="item.key"
                type="button"
                role="tab"
                class="sx-tabs__tab"
                :class="{ on: tab === item.key }"
                :aria-selected="tab === item.key ? 'true' : 'false'"
                :qid="'vendor-tab-' + item.key"
                @click="openTab(item.key)">
          <font-awesome-icon :icon="item.icon"/>{{ item.label }}<span v-if="item.count" class="sx-tabs__n">{{ item.count }}</span>
        </button>
      </nav>

      <div v-if="thirdParty.id" class="sx-tabs__content">
        <!-- Overview: its contracts, where it takes our data and the company (US 9371) -->
        <div v-show="tab === 'overview'" role="tabpanel">
          <VendorOverviewTab ref="overview"
                             :vendor="thirdParty"
                             scoped
                             @summary="summary = $event"
                             @open-tab="openTab"
                             @open-contract="pick"
                             @edit="$refs.editPanel.show()"></VendorOverviewTab>
        </div>

        <!-- Data flow: activity → system → contract → third party → sub-processor → country (US 9371), loaded when opened -->
        <div v-if="flowOpened" v-show="tab === 'data-flow'" role="tabpanel">
          <VendorDataFlowTab :vendor="thirdParty"></VendorDataFlowTab>
        </div>

        <div v-show="tab === 'contracts'" role="tabpanel">
          <div class="card pagination-margin"
               qid="data-objects-section"
          >
            <div class="card-body">
              <h5 class="mb-2" qid="do-table-title">
                {{ $t('maintenance.third_parties.vendors.tab.contracts') }}
              </h5>

              <table class="table table-responsive-md table-list-secondary">
                <thead>
                  <tr>
                    <th scope="col"
                        class="cursor-pointer"
                        @click="selectSort('name')"
                        qid="sort-tp-name"
                    >
                      {{ $t('maintenance.third_parties.table.name') }}
                      <span class="ml-2">
                        <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                        <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                        <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                      </span>
                    </th>
                    <th scope="col">
                      {{ $t('processes.table.owners') }}
                    </th>
                    <th scope="col">
                      {{ $t('maintenance.third_parties.table.vendors') }}
                    </th>
                    <th scope="col">
                      {{ $t('processes.table.tags') }}
                    </th>
                    <th scope="col"
                        class="cursor-pointer"
                        @click="selectSort('type')"
                        qid="sort-tp-type"
                    >
                      {{ $t('table.category') }}
                      <span class="ml-2">
                        <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'type'"/>
                        <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'type'"/>
                        <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'type'"/>
                      </span>
                    </th>
                    <th scope="col"
                        class="cursor-pointer"
                        @click="selectSort('updated_at')"
                        qid="sort-p-name"
                    >
                      {{ $t('system.last_updated') }}
                      <span class="ml-2">
                        <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'updated_at'"/>
                        <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'updated_at'"/>
                        <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'updated_at'"/>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="object in tableItems">
                    <td>
                      <router-link
                          :to="{ name: 'contracts.view', params: { slug: object.slug }}"
                          qid="tp-view-page-link"
                      >
                        {{object.name}}
                      </router-link>
                    </td>
                    <td>
                      <span v-if="object.users && object.users.length">
                        <span class="mr-1" v-for="user in getReducedUsers(object.users)">
                          <span qid="profile-photo">
                            <Avatar :object="user" size="xs"></Avatar>
                          </span>
                        </span>
                        <span v-if="object.users.length > 3"
                              class="badge cursor-pointer"
                              @click="openUsersModal(object)"
                        >
                          <font-awesome-icon icon="users"/>
                          + {{ object.users.length - 3 }}
                        </span>
                      </span>
                    </td>
                    <td>
                      <span class="mr-1 text-normal" v-for="item in getReducedVendors(object.vendors)">
                        <span qid="profile-photo">
                          <Avatar :object="item" size="xs"></Avatar>
                          {{ item.name }}
                        </span>
                      </span>
                      <span v-if="object.vendors.length > 3"
                            class="badge cursor-pointer"
                            @click="openVendorsModal(object)"
                      >
                      + {{ object.vendors.length - 3 }}
                      </span>
                    </td>
                    <td>
                      <span class="mr-1" v-for="tag in getReducedTags(object.tags_translated)" qid="p-tags-list">
                        <span class="badge mb-1" :title="tag.name_translated">
                          <font-awesome-icon icon="tag"/>
                          {{ tag.name_translated | truncate(35) }}
                        </span>
                      </span>
                      <span v-if="object.tags_translated.length > 3"
                            class="badge cursor-pointer"
                            @click="openTagsModal(object)"
                      >
                        <font-awesome-icon icon="tags"/>
                        + {{ object.tags_translated.length - 3 }}
                      </span>
                    </td>
                    <td>
                      <span class="badge" v-if="object.type" :title="object.type">
                        {{ object.type | truncate(35) }}
                      </span>
                    </td>
                    <td>
                      <span class="text-normal">
                        {{ object.updated_at | formatDate }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                    <td colspan="10">
                      <h5 class="mt-3">
                        {{ $t('system.no_results') }}
                      </h5>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="card-body">
              <div class="pagination row">
                <div class="col-md-6">
                  <div class="row">
                    <div class="float-left">
                      <span class="text-muted pagination-counter">
                        {{ $t('pagination.entries_per_page') }}:
                      </span>
                    </div>
                    <div class="float-left">
                      <multiselect
                          v-model="perPage"
                          :options="perPageOptions"
                          track-by="value"
                          label="value"
                          :select-label="$t('system.dropdown_select_label')"
                          :selected-label="$t('system.dropdown_selected_label')"
                          :deselect-label="$t('system.dropdown_deselect_label')"
                          qid="create-org-form-organization-level"
                          @select="perPageChanged"
                          :placeholder="selectedPerPage"
                          @remove="perPageRemoved"
                      ></multiselect>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <b-pagination
                      class="float-right"
                      v-model="currentPage"
                      :total-rows="rows"
                      :per-page="selectedPerPage"
                      aria-controls="my-table"
                      @change="pageChanged"
                      limit="1"
                  >
                    <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
                    <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
                    <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
                    <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
                    <div slot="ellipsis-text">
                      <b-spinner small type="grow"></b-spinner>
                      <b-spinner small type="grow"></b-spinner>
                      <b-spinner small type="grow"></b-spinner>
                    </div>
                    <span class="text-muted" slot="page" slot-scope="{ page, active }">
                      <span v-if="active">{{getPerPage}}</span>
                    </span>
                  </b-pagination>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Its own sub-processors, and the contracts of others it is a sub-processor on -->
        <div v-if="getClient.gdprAdvancedModule" v-show="tab === 'sub-processors'" role="tabpanel">
          <div class="card pagination-margin"
               qid="data-objects-section"
          >
            <div class="card-body">
              <h5 class="mb-2" qid="do-table-title">
                {{ $t('maintenance.third_parties.vendors.tab.subdata_processor') }}
              </h5>

              <table class="table table-responsive-md table-list-secondary">
                <thead>
                <tr>
                  <th scope="col"
                      class="cursor-pointer"
                      @click="selectProcessorSort('name')"
                      qid="sort-tp-name"
                  >
                    {{ $t('maintenance.third_parties.table.name') }}
                    <span class="ml-2">
                      <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                      <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                      <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                    </span>
                  </th>
                  <th scope="col">
                    {{ $t('processes.table.owners') }}
                  </th>
                  <th scope="col">
                    {{ $t('systems.third_parties.create.tp_label') }}
                  </th>
                  <th scope="col">
                    {{ $t('processes.table.tags') }}
                  </th>
                  <th scope="col"
                      class="cursor-pointer"
                      @click="selectProcessorSort('type')"
                      qid="sort-tp-type"
                  >
                    {{ $t('table.category') }}
                    <span class="ml-2">
                      <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'type'"/>
                      <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'type'"/>
                      <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'type'"/>
                    </span>
                  </th>
                  <th scope="col"
                      class="cursor-pointer"
                      @click="selectProcessorSort('updated_at')"
                      qid="sort-p-name"
                  >
                    {{ $t('system.last_updated') }}
                    <span class="ml-2">
                      <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'updated_at'"/>
                      <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'updated_at'"/>
                      <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'updated_at'"/>
                    </span>
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="object in tableProcessorItems">
                  <td>
                    <router-link
                        :to="{ name: 'contracts.third_parties.view', params: { slug: object.vendor.slug }}"
                        qid="tp-view-page-link"
                    >
                      <Avatar :object="object.vendor" size="xs"></Avatar>
                      {{object.vendor.name}}
                    </router-link>
                  </td>
                  <td>
                    <span v-if="object.third_party.users && object.third_party.users.length">
                      <span class="mr-1" v-for="user in getReducedUsers(object.third_party.users)">
                        <span qid="profile-photo">
                          <Avatar :object="user" size="xs"></Avatar>
                        </span>
                      </span>
                      <span v-if="object.third_party.users.length > 3"
                            class="badge cursor-pointer"
                            @click="openUsersModal(object.third_party)"
                      >
                        <font-awesome-icon icon="users"/>
                        + {{ object.third_party.users.length - 3 }}
                      </span>
                    </span>
                  </td>
                  <td>
                    <span class="mr-1 text-normal">
                      <span qid="profile-photo">
                        {{ object.third_party.name }}
                      </span>
                    </span>
                  </td>
                  <td>
                    <span class="mr-1" v-for="tag in getReducedTags(object.third_party.tags_translated)" qid="p-tags-list">
                      <span class="badge mb-1" :title="tag.name_translated">
                        <font-awesome-icon icon="tag"/>
                        {{ tag.name_translated | truncate(35) }}
                      </span>
                    </span>
                    <span v-if="object.third_party.tags_translated.length > 3"
                          class="badge cursor-pointer"
                          @click="openTagsModal(object.third_party)"
                    >
                      <font-awesome-icon icon="tags"/>
                      + {{ object.third_party.tags_translated.length - 3 }}
                    </span>
                  </td>
                  <td>
                    <span class="badge" v-if="object.third_party.type" :title="object.third_party.type">
                      {{ object.third_party.type | truncate(35) }}
                    </span>
                  </td>
                  <td>
                    <span class="text-normal">
                      {{ object.third_party.updated_at | formatDate }}
                    </span>
                  </td>
                </tr>
                <tr v-if="!vendorsLoading && !tableProcessorItems.length" qid="no-results-row" class="text-center">
                  <td colspan="10">
                    <h5 class="mt-3">
                      {{ $t('system.no_results') }}
                    </h5>
                  </td>
                </tr>
                <tr v-if="vendorsLoading" qid="no-results-row-rr" class="text-center">
                  <td colspan="10">
                    <div class="mt-2 mb-4">
                      <div class="swing-container">
                        <div class="swing">
                          <div class="swing-l"></div>
                          <div></div>
                          <div></div>
                          <div></div>
                          <div class="swing-r"></div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <div class="card-body">
              <div class="pagination row">
                <div class="col-md-6">
                  <div class="row">
                    <div class="float-left">
                      <span class="text-muted pagination-counter">
                        {{ $t('pagination.entries_per_page') }}:
                      </span>
                    </div>
                    <div class="float-left">
                      <multiselect
                          v-model="perPage"
                          :options="perPageOptions"
                          track-by="value"
                          label="value"
                          :select-label="$t('system.dropdown_select_label')"
                          :selected-label="$t('system.dropdown_selected_label')"
                          :deselect-label="$t('system.dropdown_deselect_label')"
                          qid="create-org-form-organization-level"
                          @select="processorPerPageChanged"
                          :placeholder="selectedPerPage"
                          @remove="processorPerPageRemoved"
                      ></multiselect>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <b-pagination
                      class="float-right"
                      v-model="currentPage"
                      :total-rows="processorRows"
                      :per-page="selectedPerPage"
                      aria-controls="my-table"
                      @change="processorPageChanged"
                      limit="1"
                  >
                    <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
                    <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
                    <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
                    <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
                    <div slot="ellipsis-text">
                      <b-spinner small type="grow"></b-spinner>
                      <b-spinner small type="grow"></b-spinner>
                      <b-spinner small type="grow"></b-spinner>
                    </div>
                    <span class="text-muted" slot="page" slot-scope="{ page, active }">
                      <span v-if="active">{{processorGetPerPage}}</span>
                    </span>
                  </b-pagination>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-3"></div>
          <div class="card pagination-margin"
               qid="data-objects-section"
          >
            <div class="card-body">
              <h5 class="mb-2" qid="do-table-title">
                {{ $t('maintenance.third_parties.vendors.tab.subdata_processor') }}
              </h5>

              <table class="table table-responsive-md table-list-secondary">
                <thead>
                <tr>
                  <th scope="col"
                      class="cursor-pointer"
                      @click="selectProcessorSort('name')"
                      qid="sort-tp-name"
                  >
                    {{ $t('maintenance.third_parties.table.name') }}
                    <span class="ml-2">
                      <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                      <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                      <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                    </span>
                  </th>
                  <th scope="col">
                    {{ $t('processes.table.owners') }}
                  </th>
                  <th scope="col">
                    {{ $t('maintenance.third_parties.table.vendors') }}
                  </th>
                  <th scope="col">
                    {{ $t('processes.table.tags') }}
                  </th>
                  <th scope="col"
                      class="cursor-pointer"
                      @click="selectProcessorSort('type')"
                      qid="sort-tp-type"
                  >
                    {{ $t('table.category') }}
                    <span class="ml-2">
                      <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'type'"/>
                      <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'type'"/>
                      <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'type'"/>
                    </span>
                  </th>
                  <th scope="col"
                      class="cursor-pointer"
                      @click="selectProcessorSort('updated_at')"
                      qid="sort-p-name"
                  >
                    {{ $t('system.last_updated') }}
                    <span class="ml-2">
                      <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'updated_at'"/>
                      <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'updated_at'"/>
                      <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'updated_at'"/>
                    </span>
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="object in tableProcessorContractsItems">
                  <td>
                    <router-link
                        :to="{ name: 'contracts.view', params: { slug: object.third_party.slug }}"
                        qid="tp-view-page-link"
                    >
                      {{object.third_party.name}}
                    </router-link>
                  </td>
                  <td>
                    <span v-if="object.third_party.users && object.third_party.users.length">
                      <span class="mr-1" v-for="user in getReducedUsers(object.third_party.users)">
                        <span qid="profile-photo">
                          <Avatar :object="user" size="xs"></Avatar>
                        </span>
                      </span>
                      <span v-if="object.third_party.users.length > 3"
                            class="badge cursor-pointer"
                            @click="openUsersModal(object.third_party)"
                      >
                        <font-awesome-icon icon="users"/>
                        + {{ object.third_party.users.length - 3 }}
                      </span>
                    </span>
                  </td>
                  <td>
                    <span class="mr-1 text-normal" v-for="item in getReducedVendors(object.third_party.vendors)">
                      <span qid="profile-photo">
                        <Avatar :object="item" size="xs"></Avatar>
                        {{ item.name }}
                      </span>
                    </span>
                    <span v-if="object.third_party.vendors.length > 3"
                          class="badge cursor-pointer"
                          @click="openVendorsModal(object.third_party)"
                    >
                      + {{ object.third_party.vendors.length - 3 }}
                    </span>
                  </td>
                  <td>
                    <span class="mr-1" v-for="tag in getReducedTags(object.third_party.tags_translated)" qid="p-tags-list">
                      <span class="badge mb-1" :title="tag.name_translated">
                        <font-awesome-icon icon="tag"/>
                        {{ tag.name_translated | truncate(35) }}
                      </span>
                    </span>
                    <span v-if="object.third_party.tags_translated.length > 3"
                          class="badge cursor-pointer"
                          @click="openTagsModal(object.third_party)"
                    >
                      <font-awesome-icon icon="tags"/>
                      + {{ object.third_party.tags_translated.length - 3 }}
                    </span>
                  </td>
                  <td>
                    <span class="badge" v-if="object.third_party.type" :title="object.third_party.type">
                      {{ object.third_party.type | truncate(35) }}
                    </span>
                  </td>
                  <td>
                    <span class="text-normal">
                      {{ object.third_party.updated_at | formatDate }}
                    </span>
                  </td>
                </tr>
                <tr v-if="!loading && !tableProcessorContractsItems.length" qid="no-results-row" class="text-center">
                  <td colspan="10">
                    <h5 class="mt-3">
                      {{ $t('system.no_results') }}
                    </h5>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <div class="card-body">
              <div class="pagination row">
                <div class="col-md-6">
                  <div class="row">
                    <div class="float-left">
                      <span class="text-muted pagination-counter">
                        {{ $t('pagination.entries_per_page') }}:
                      </span>
                    </div>
                    <div class="float-left">
                      <multiselect
                          v-model="perPage"
                          :options="perPageOptions"
                          track-by="value"
                          label="value"
                          :select-label="$t('system.dropdown_select_label')"
                          :selected-label="$t('system.dropdown_selected_label')"
                          :deselect-label="$t('system.dropdown_deselect_label')"
                          qid="create-org-form-organization-level"
                          @select="processorPerPageChanged"
                          :placeholder="selectedPerPage"
                          @remove="processorPerPageRemoved"
                      ></multiselect>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <b-pagination
                      class="float-right"
                      v-model="currentPage"
                      :total-rows="processorRows"
                      :per-page="selectedPerPage"
                      aria-controls="my-table"
                      @change="processorPageChanged"
                      limit="1"
                  >
                    <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
                    <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
                    <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
                    <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
                    <div slot="ellipsis-text">
                      <b-spinner small type="grow"></b-spinner>
                      <b-spinner small type="grow"></b-spinner>
                      <b-spinner small type="grow"></b-spinner>
                    </div>
                    <span class="text-muted" slot="page" slot-scope="{ page, active }">
                      <span v-if="active">{{processorGetPerPage}}</span>
                    </span>
                  </b-pagination>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div v-show="tab === 'history'" role="tabpanel">
          <div v-if="showHistoryTab">
            <VendorHistory :object="thirdParty"></VendorHistory>
          </div>
        </div>
      </div>
      </div>


      <!-- Edit third party modal template -->
      <b-modal ref="edit-third-party" hide-footer>
        <div class="d-block text-center">
          <h4 qid="update-tp-title">
            {{ $t('maintenance.third_parties.vendors.update.title') }}
          </h4>
        </div>
        <b-alert v-if="error.length"
                 show
                 variant="danger"
                 qid="edit-tp-error"
        >
          {{ error }}
        </b-alert>
        <b-form @submit="editTPFormSubmit" qid="edit-tp-form">
          <div class="form-container">
            <b-form-group>
              <span class="align-middle mr-4">
                <img v-bind:src="getAvatar"
                     class="rounded-circle"
                     width="120"
                     height="120"
                     qid="profile-photo"
                >
              </span>
              <span class="align-middle">
                <label class="btn btn-secondary cursor-pointer pl-2 pr-2">
                  <input type="file"
                         name="fileupload"
                         value="fileupload"
                         id="file"
                         @change="onFileChange"
                  >
                  <span>
                    <font-awesome-icon icon="cloud-upload-alt" />
                    {{ $t('maintenance.third_parties.vendors.upload_picture') }}
                  </span>
                </label>
              </span>
            </b-form-group>
            <b-form-group>
              <label qid="edit-tp-name-label">
                {{ $t('maintenance.third_parties.create.name_label') }}
              </label>
              <span class="text-red">*</span>
              <b-form-input
                qid="edit-tp-form-name"
                type="text"
                v-model="tpForm.name"
                :maxLength="255"
                required
                :placeholder="$t('maintenance.third_parties.create.name_placeholder')"
              ></b-form-input>
            </b-form-group>

          </div>
          <div class="text-right">
            <b-button class="mt-3"
                      variant="secondary"
                      @click="hideEditTPModal"
                      qid="edit-tp-cancel-button"
            >
              {{ $t('maintenance.third_parties.create.cancel') }}
            </b-button>
            <b-button type="submit"
                      class="mt-3 ml-3"
                      variant="success"
                      :disabled="buttonDisabled"
                      qid="edit-tp-submit-button"
                      v-if="isGranted([permissions.THIRD_PARTY_EDIT, permissions.THIRD_PARTY_EDIT_OWN], thirdParty) && (!getClient.parent || (getClient.parent && thirdParty.partition))"
            >
              <span v-if="buttonDisabled" class="mr-1">
                <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
              </span>
              {{ $t('maintenance.third_parties.create.submit') }}
            </b-button>
          </div>
        </b-form>
      </b-modal>


      <!-- Delete third party confirm modal template -->
      <b-modal ref="delete-third-party" hide-footer>
        <div class="d-block text-center">
          <h4 qid="delete-tp-title">
            {{ $t('maintenance.third_parties.delete.title') }}
          </h4>
        </div>
        <h5 class="text-center">
          {{ $t('maintenance.third_parties.delete.description') }} {{ itemToDelete.name }}
        </h5>
        <h5 class="text-center">
          {{ $t('maintenance.third_parties.delete.are_you_sure') }}
        </h5>

        <h6 class="mt-2" v-if="tableItems && tableItems.length && itemToDelete && itemToDelete.id">
          {{ $t('maintenance.third_parties.delete.contracts_notice') }}
        </h6>
        <table class="table table-responsive-md" v-if="tableItems && tableItems.length && itemToDelete && itemToDelete.id">
          <thead>
          <tr>
            <th>
              {{ $t('maintenance.third_parties.table.name') }}
            </th>
            <th>
              {{ $t('system.last_updated') }}
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="object in tableItems" v-if="tableItems && tableItems.length">
            <td>
              <router-link
                  :to="{ name: 'contracts.view', params: { slug: object.slug }}"
                  qid="tp-view-page-link"
              >
                {{object.name}}
              </router-link>
            </td>
            <td>
              <span>
                {{ object.updated_at | formatDate }}
              </span>
            </td>
          </tr>
          </tbody>
        </table>

        <div class="text-center">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideConfirmDeleteModal"
                    qid="delete-tp-cancel-button"
          >
            {{ $t('maintenance.third_parties.delete.cancel') }}
          </b-button>
          <b-button @click="confirmDeleteTP(itemToDelete)"
                    class="mt-3 ml-3"
                    variant="success"
                    qid="delete-tp-submit-button"
                    v-if="isGranted([permissions.THIRD_PARTY_DELETE, permissions.THIRD_PARTY_DELETE_OWN], itemToDelete) && (!getClient.parent || (getClient.parent && itemToDelete.partition)) && !tableItems.length"
          >
            {{ $t('maintenance.third_parties.delete.submit') }}
          </b-button>
        </div>
      </b-modal>


      <!-- Crop image modal -->
      <b-modal ref="crop-image" hide-footer size="lg">
        <div class="d-block text-center">
          <h4 qid="edit-profile-modal-title">
            {{ $t('system.crop_image') }}
          </h4>
        </div>
        <div class="form-container" style="max-height: 600px;">
          <img v-bind:src="imageToCrop"
               qid="profile-photo"
               id="crop"
          >
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideCropModal"
                    qid="edit-profile-cancel-button"
          >
            {{ $t('profile.password.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    id="crop-button"
                    :disabled="buttonDisabled"
                    qid="edit-profile-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('profile.password.submit') }}
          </b-button>
        </div>
      </b-modal>


      <ConfirmRemoveAttachment
          :object="selectedFile"
          :show="showDeleteAttachmentModal"
          type="file"
          @closeConfirmModal="closeDeleteAttachmentModal"
      ></ConfirmRemoveAttachment>

      <ConfirmRemoveAttachment
          :object="selectedLink"
          :show="showDeleteLinkModal"
          type="link"
          @closeConfirmModal="closeDeleteLinkModal"
      ></ConfirmRemoveAttachment>

      <ShowMoreModal
          :items="selectedItem.tags_translated"
          :show="showTagsModal"
          @closeViewModal="closeTagsModal"
          type="tags"
      ></ShowMoreModal>

      <ShowMoreModal
          :items="selectedItem.users"
          :show="showUsersModal"
          @closeViewModal="closeUsersModal"
          type="users"
      ></ShowMoreModal>

      <ShowMoreModal
          :items="selectedItem.vendors"
          :show="showVendorsModal"
          @closeViewModal="closeVendorsModal"
          type="vendors"
      ></ShowMoreModal>
    </div>


    <div v-if="show404">
      <page404></page404>
    </div>

    <div v-if="show403">
      <page403></page403>
    </div>

  </div>
</template>

<script>
import {initialsAvatarUrl} from '@/util/userAvatar.js';
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'
import Datepicker from 'vuejs-datepicker';
import Avatar from '@/components/Avatar.vue';
import VendorHero from '@/views/ThirdParty/page/VendorHero.vue';
import VendorSwitcher from '@/views/ThirdParty/page/VendorSwitcher.vue';
import VendorOverviewTab from '@/views/ThirdParty/page/VendorOverviewTab.vue';
import VendorEditPanel from '@/views/ThirdParty/page/VendorEditPanel.vue';
import VendorDataFlowTab from '@/views/ThirdParty/page/VendorDataFlowTab.vue';
import ThirdPartiesView from '@/views/ThirdParty/ThirdPartiesView.vue';
import V2SidePanel from '@/components/V2SidePanel.vue';
import V2LogoPicker from '@/components/V2LogoPicker.vue';
import ConfirmRemoveAttachment from '@/components/ConfirmRemoveAttachment.vue';
import VendorHistory from '@/views/ThirdParty/VendorHistory.vue';
import ShowMoreModal from '@/components/ShowMoreModal.vue';
import GuideButton from '@/components/guide/GuideButton.vue';
import page404 from '@/components/404block.vue';
import page403 from '@/components/403block.vue';

import VueCropper from 'vue-cropperjs';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';

export default {
  name: 'VendorView',
  data: function() {
    return {
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        },
        {
          text: this.$t('maintenance.third_parties.vendors.title'),
          to: '/third-parties'
        }
      ],
      thirdParty: {},
      showInput: false,
      showSingleLinkLabel: true,
      descriptionLength: config.descriptionLength,
      edit: this.$route.params.edit,
      inputLink: '',
      showFullDescription: false,
      showFullContact: false,
      itemToDelete: {},
      tpForm: {
        name: '',
        description: '',
        file: {}
      },
      addressForm: {
        company_number: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
      },
      showForm: false,
      error: '',
      types: [],
      typeInput: '',
      buttonDisabled: false,
      showDescriptionInput: false,
      processEditDescription: '',
      showContactInput: false,
      processEditContact: '',
      selectedFile: {},
      selectedLink: {},
      showDeleteAttachmentModal: false,
      showDeleteLinkModal: false,
      tabIndex: 0,
      // The open tab of our tab bar, and counts from the Overview (US 9371)
      tab: 'overview',
      summary: null,
      flowOpened: false,
      // The row under the hero and what's picked in it: null = the third party, or { id, slug } of a contract (US 9369 + 9371)
      row: null,
      picked: null,
      ready: false,
      myGuides: [],
      selectedTags: [],
      tags: [],
      users: [],
      newTags: [],
      tableItems: [],
      tableProcessorItems: [],
      tableProcessorContractsItems: [],
      loading: false,
      selectedDO: [],
      selectedUsers: [],
      selectedDataObject: {},
      viewDataObjectActivitiesModal: false,
      selectedDataObjectActivities: [],
      showHistoryTab: false,
      showRiskTab: false,
      showDataObjectDescription: false,
      // Pagination
      perPageOptions: [
        { name: '10', value: '10' },
        { name: '20', value: '20' },
        { name: '50', value: '50' },
        { name: '100', value: '100' }
      ],
      perPage: '',
      selectedPerPage: "10",
      currentPage: 1,
      totalRows: 0,
      totalProcessorRows: 0,
      keywordValue: '',
      selectedSort: '',
      selectedDirection: '',
      myTasks: [],
      selectedItem: {},
      showTagsModal: false,
      showUsersModal: false,
      viewDataModal: false,
      showVendorsModal: false,
      showAssessmentsTab: false,
      imageToCrop: '',
      avatar: '',
      avatarLarge: '',
      croppedBlob: {},
      countries: [],
      selectedCountry: [],
      show404: false,
      show403: false,
      vendorsLoading: true
    }
  },
  components: {
    Datepicker,
    Avatar,
    VendorHero,
    VendorSwitcher,
    VendorOverviewTab,
    VendorEditPanel,
    VendorDataFlowTab,
    ThirdPartiesView,
    V2SidePanel,
    V2LogoPicker,
    ConfirmRemoveAttachment,
    VendorHistory,
    ShowMoreModal,
    GuideButton,
    page404,
    page403
  },
  computed: {
    // Its own contracts; shared ones from before live on another third party's page
    ownContracts() {
      return this.row ? this.row.children.filter(child => !child.shared) : [];
    },
    pickedIndex() {
      let ids = this.ownContracts.map(child => child.id);
      return this.picked ? ids.indexOf(this.picked.id) + 1 : 0;
    },
    canAddContract() {
      return this.isGranted(this.permissions.THIRD_PARTY_CREATE);
    },
    tabItems() {
      let counts = (this.summary && this.summary.counts) || {};
      return [
        { key: 'overview', icon: 'th-large', label: this.$t('system_page.tab.overview') },
        { key: 'contracts', icon: 'file-alt', label: this.$t('maintenance.third_parties.vendors.tab.contracts'), count: counts.contracts },
        { key: 'data-flow', icon: 'project-diagram', label: this.$t('vendor_flow.tab'), module: this.getClient.gdprModule },
        { key: 'sub-processors', icon: 'users', label: this.$t('vendor_page.tab.sub_processors'), count: (counts.sub_processors || 0) + (counts.sub_processor_on || 0), module: this.getClient.gdprAdvancedModule },
        { key: 'history', icon: 'history', label: this.$t('maintenance.third_parties.tab.history') }
      ].filter(item => item.module === undefined || item.module);
    },
    rows() {
      // Use computed value for rows because of dynamic update
      return this.totalRows
    },
    processorRows() {
      // Use computed value for rows because of dynamic update
      return this.totalProcessorRows
    },
    getClient() {
      return this.$store.state.client
    },
    getAvatar(){
      if (this.avatarLarge) {
        return this.avatarLarge;
      } else {
        return require('@/assets/tp-logo.png');
      }
    },
    getTabIndex() {
      return this.$store.state.tabIndex
    },
    getStorePerPage() {
      return this.$store.state.perPage
    },
    getPerPage() {
      let page = this.currentPage;
      let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
      let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
      let selectedOf = this.totalRows;

      if(selectedTo > selectedOf){
        selectedTo = selectedOf
      }

      return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
    },
    processorGetPerPage() {
      let page = this.currentPage;
      let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
      let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
      let selectedOf = this.totalProcessorRows;

      if(selectedTo > selectedOf){
        selectedTo = selectedOf
      }

      return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
    },
    getReloadContent() {
      return this.$store.state.updateContent
    },
    getReloadPage() {
      return this.$store.state.updatePage
    }
  },
  watch: {
    getClient: function (newValue, oldValue) {
      if (newValue && oldValue && newValue.slug && oldValue.slug && newValue.slug !== oldValue.slug) {
        let self = this;
        setTimeout(function(){
          if (self.$route.name == 'contracts.third_parties.view') {
            self.goToList()
          }
        }, 800);
      } else {
        this.loadContent()
      }
    },
    getTabIndex: function (newValue, oldValue) {
      this.handleUpdateTab(newValue)
    }
  },
  methods: {
    // Which item opens first: one contract → that contract; two or more → the third party; none → the third party.
    // ?contract=<slug> picks one (old contract links redirect here with it); ?view=third-party keeps the third party
    // (as ?view=process on the process page).
    rowLoaded(row) {
      this.row = row;
      if (this.ready) {
        return;
      }
      let wanted = this.$route.query.contract;
      let children = row ? row.children : [];
      let child = wanted
        ? children.find(item => item.slug === wanted && !item.shared)
        : (this.ownContracts.length === 1 && this.$route.query.view !== 'third-party' ? this.ownContracts[0] : null);
      this.picked = child ? { id: child.id, slug: child.slug } : null;
      this.ready = true;
    },
    pick(child) {
      if (child === 'parent') {
        this.picked = null;
      } else {
        let found = (this.row && this.row.children.find(item => item.id === child.id)) || child;
        // A shared contract from before lives on another third party's page
        if (found.shared) {
          this.$router.push({ name: 'contracts.view', params: { slug: found.slug } });
          return;
        }
        this.picked = { id: found.id, slug: found.slug };
      }
      this.writeUrl();
    },
    // The address keeps what's picked. Not through the router: the page is keyed by its full path and would be rebuilt.
    writeUrl() {
      let query = Object.assign({}, this.$route.query);
      delete query.contract;
      delete query.view;
      if (this.picked) {
        query.contract = this.picked.slug;
      } else if (this.ownContracts.length === 1) {
        query.view = 'third-party';
      }
      let route = this.$router.resolve({ name: 'contracts.third_parties.view', params: { slug: this.$route.params.slug }, query: query });
      window.history.replaceState(window.history.state, '', route.href);
    },
    addContract() {
      this.$router.push({ name: 'contracts.create', query: { vendor: this.thirdParty.slug } });
    },
    // The picked contract was edited, ended or reopened: the row and the third party's summary follow
    contractChanged() {
      this.$refs.switcher && this.$refs.switcher.reload();
      this.$refs.overview && this.$refs.overview.load();
    },
    // A shared contract was decided on (US 9369 + 9371): the summary and contracts follow
    sharedDecided() {
      this.$refs.overview && this.$refs.overview.load();
      this.loadContracts();
    },
    // Moved to another third party: open it there
    contractMoved() {
      this.$router.push({ name: 'contracts.view', params: { slug: this.picked.slug } });
    },
    contractDeleted() {
      this.picked = null;
      this.writeUrl();
      this.contractChanged();
      this.loadContracts();
    },
    // The logo panel saved straight away: show it in the hero and in the edit form
    logoSaved(data) {
      this.thirdParty = Object.assign({}, this.thirdParty, { logo: data.logo, logo_large: data.logo_large });
      this.avatar = data.logo_large || data.logo || '';
      this.avatarLarge = this.avatar;
    },
    debounceInput: _.debounce(function() {
      this.loadContracts()
    }, 400),
    getReducedTags(tags) {
      let reducedTags = []
      if (tags.length > 3) {
        reducedTags = _.cloneDeep(tags);
        reducedTags = reducedTags.slice(0, 3);
      } else {
        reducedTags = tags;
      }
      return reducedTags;
    },
    getReducedUsers(users) {
      let reducedUsers = []
      if (users) {
        if (users.length > 3) {
          reducedUsers = _.cloneDeep(users);
          reducedUsers = reducedUsers.slice(0, 3);
        } else {
          reducedUsers = users;
        }
      }
      return reducedUsers;
    },
    openTagsModal(object) {
      this.selectedItem = object
      this.showTagsModal = true
    },
    closeTagsModal() {
      this.selectedItem = {}
      this.showTagsModal = false
    },
    openUsersModal(object) {
      this.selectedItem = object
      this.showUsersModal = true
    },
    closeUsersModal() {
      this.selectedItem = {}
      this.showUsersModal = false
    },
    getReducedVendors(vendors) {
      let reducedVendors = []
      if (vendors) {
        if (vendors.length > 3) {
          reducedVendors = _.cloneDeep(vendors);
          reducedVendors = reducedVendors.slice(0, 3);
        } else {
          reducedVendors = vendors;
        }
      }
      return reducedVendors;
    },
    openVendorsModal(object) {
      this.selectedItem = object
      this.showVendorsModal = true
    },
    closeVendorsModal() {
      this.selectedItem = {}
      this.showVendorsModal = false
    },
    showFormFunction() {
      let self = this;

      this.addressForm.company_number = this.thirdParty.company_number;
      this.addressForm.address = this.thirdParty.address;
      this.addressForm.city = this.thirdParty.city;
      this.addressForm.state = this.thirdParty.state;
      this.addressForm.postal_code = this.thirdParty.postal_code;

      self.selectedCountry = [];
      if (this.thirdParty.country) {
        _.find(this.countries, function(item) {
          if (item.name === self.thirdParty.country) {
            self.selectedCountry = item
          }
        });
      }

      this.addressForm.error = '';
      this.showForm = true;
    },
    hideFormFunction() {
      this.showForm = false;
    },
    addressFormSubmit() {
      this.buttonDisabled = true;
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;

      if (this.selectedCountry && this.selectedCountry.name) {
        this.addressForm.country = this.selectedCountry.name
      }

      piincHttp.put('vendors/' + this.thirdParty.slug, this.addressForm).then(function(response) {
        let client = response.data;
        self.buttonDisabled = false;
        self.hideFormFunction();
        self.breadcrumbItems.pop();

        self.loadContent();
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.addressForm.error = error.data.message;
        }
      })
    },
    showCropModal() {
      let self = this

      this.$refs['crop-image'].show()
      this.buttonDisabled = false;

      setTimeout(function(){
        const image = document.getElementById('crop');
        const cropper = new Cropper(image, {
          aspectRatio: 1,
          viewMode: 2,
          crop(event) {},
        });

        document.getElementById('crop-button').addEventListener('click', function(){
          cropper.getCroppedCanvas().toBlob(function (blob) {

            self.avatar = URL.createObjectURL(blob);
            self.avatarLarge = URL.createObjectURL(blob);
            self.imageToCrop = URL.createObjectURL(blob);
            self.croppedBlob = blob

            self.hideCropModal()

          });
        })
      }, 500);

    },
    hideCropModal() {
      this.$refs['crop-image'].hide()
    },
    onFileChange(file) {
      this.tpForm.file = file;
      const avatar = file.target.files[0];
      this.avatar = URL.createObjectURL(avatar);
      this.avatarLarge = URL.createObjectURL(avatar);
      this.imageToCrop = URL.createObjectURL(avatar);

      this.showCropModal()

    },
    selectSort(sort) {
      // If old and new sort are same, switch just direction
      if (sort === this.selectedSort) {
        this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
      } else {
        // If different sort, set sort and set direction to default
        this.selectedDirection = 'desc'
        this.selectedSort = sort;
      }
      this.loadContracts();
    },
    selectProcessorSort(sort) {
      // If old and new sort are same, switch just direction
      if (sort === this.selectedSort) {
        this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
      } else {
        // If different sort, set sort and set direction to default
        this.selectedDirection = 'desc'
        this.selectedSort = sort;
      }
      this.loadProcessors();
    },
    pageChanged(page) {
      this.loadContracts(page)
    },
    perPageChanged(perPage) {
      let self = this;
      // Load content when page changed listener is triggered
      this.loadContracts(1, perPage.value)
      self.selectedPerPage = perPage.value
      this.$store.commit('storePerPage', this.selectedPerPage);
    },
    perPageRemoved() {
      let self = this;
      // Load content when page changed listener is triggered
      self.selectedPerPage = self.perPageOptions[0].value
      this.loadContracts(1, self.selectedPerPage)
    },
    processorPageChanged(page) {
      this.loadProcessors(page)
    },
    processorPerPageChanged(perPage) {
      let self = this;
      // Load content when page changed listener is triggered
      this.loadProcessors(1, perPage.value)
      self.selectedPerPage = perPage.value
      this.$store.commit('storePerPage', this.selectedPerPage);
    },
    processorPerPageRemoved() {
      let self = this;
      // Load content when page changed listener is triggered
      self.selectedPerPage = self.perPageOptions[0].value
      this.loadProcessors(1, self.selectedPerPage)
    },
    goToList() {
      this.$router.push('/third-parties')
    },
    openTab(key) {
      if (!this.tabItems.some(item => item.key === key)) {
        return;
      }
      this.tab = key;
      if (key === 'data-flow') {
        this.flowOpened = true;
      }
      if (key === 'history') {
        this.historyTabClicked();
      }
    },
    // Saved in the Edit details panel; a new name gives a new address
    detailsSaved(vendor) {
      if (vendor.slug && vendor.slug !== this.thirdParty.slug) {
        this.$router.replace({ name: 'contracts.third_parties.view', params: { slug: vendor.slug } });
        return;
      }
      this.loadContent();
    },
    historyTabClicked() {
      this.showAssessmentsTab = false;
      this.showHistoryTab = true;
      this.showRiskTab = false;
    },
    resetHistoryTab() {
      this.showHistoryTab = false;
      this.showRiskTab = false;
    },
    getTask(code) {
      let existingTask = _.find(this.myTasks, function(object) {
        return object.code == code
      });
      return existingTask
    },
    openToolbar(object) {
      this.$store.commit('storeTaskToolbarState', true);
      this.$store.commit('storeActiveTask', object);
    },
    getGuide(code) {
      let existingGuide = _.find(this.myGuides, function(object) {
        return object.scope == code && object.type == 'Vendor' && object.enabled
      });
      return existingGuide
    },
    loadGuides(code) {
      let self = this;
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

      piincHttp.get('guides', { params:
        {
          per_page: 1,
          language: 'en',
          type: 'Vendor',
          scope: code,
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides.push(response.data.items[0])
        }
      }, function() {});
    },
    showDescriptionInputFunction() {
      this.showDescriptionInput = true;
      this.processEditDescription = this.thirdParty.description;
    },
    formDescriptionSubmit() {
      let self = this;
      piincHttp.put('vendors/' + this.$route.params.slug, {
        description: this.processEditDescription
      }).then(function(response) {
        self.thirdParty = response.data;
        self.showDescriptionInput = false;
        self.$toastr('success', self.$t('maintenance.third_parties.vendors.update.success'))
      }, function(error) {
        if (error.status !== 200) {
          self.error = error.data.message;
        }
      })
    },
    cancelDescriptionForm() {
      this.showDescriptionInput = false;
      this.processEditDescription = this.thirdParty.description;
    },
    resetFile() {
      let imagefile = document.querySelector('#file');
      imagefile.value = '';
    },
    openDeleteAttachmentModal(object) {
      this.selectedFile = object
      this.showDeleteAttachmentModal = true
    },
    closeDeleteAttachmentModal () {
      this.selectedFile = {}
      this.showDeleteAttachmentModal = false;
      // Remove last item in breadcrumbs, because it will be added again on load content
      this.breadcrumbItems.pop();
      this.loadContent();
    },
    openDeleteLinkModal(object) {
      this.selectedLink = object
      this.showDeleteLinkModal = true
    },
    closeDeleteLinkModal () {
      this.selectedLink = {}
      this.showDeleteLinkModal = false;
      // Remove last item in breadcrumbs, because it will be added again on load content
      this.breadcrumbItems.pop();
      this.loadContent();
    },
    handleLink() {
      let self = this;
      let formData = new FormData();

      if (!this.inputLink.startsWith('http')) {
        this.inputLink = 'http://' + this.inputLink
      }

      formData.append("url", this.inputLink);
      formData.append("model_type", this.thirdParty.who_am_i);
      formData.append("model_id", this.thirdParty.id);

      piincHttp.post('links', formData).then(function() {
        // Remove last item in breadcrumbs, because it will be added again on load content
        self.breadcrumbItems.pop();
        self.$toastr('success', self.$t('maintenance.third_parties.link_add_success'))
        self.loadContent()
        self.inputLink = ''
        self.showInput = false
      }, function() {})
    },
    downloadMedia(object){
      piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = object.file_name;

        link.click();
        link.remove()
        window.URL.revokeObjectURL(url);
      }, function() {});
    },
    getUserAvatar(user){
      if (user && user.avatar) {
        return user.avatar;
      } else {
        return initialsAvatarUrl(user);
      }
    },
    showConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.$refs['delete-third-party'].show()
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {}
      this.$refs['delete-third-party'].hide()
    },
    confirmDeleteTP(object) {
      let self = this;
      piincHttp.delete('vendors/' + object.slug).then(function() {
        self.hideConfirmDeleteModal();
        self.goToList();
        self.$store.commit('storeUpdateTasks', true);
        self.$store.commit('storeActiveTask', {});
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('maintenance.third_parties.vendors.delete.success'))
      }, function() {});
    },
    showEditTPModal(object) {
      let self = this;
      this.error = ''
      this.tpForm.slug = object.slug;
      this.tpForm.name = object.name;
      self.avatar = object.logo
      self.avatarLarge = object.logo
      this.$refs['edit-third-party'].show()
    },
    hideEditTPModal() {
      this.$refs['edit-third-party'].hide()
    },
    editTPFormSubmit(evt) {
      let sizeError = false;
      var moment = require('moment');
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      evt.preventDefault();

      var formData = new FormData();

      if (this.croppedBlob.size > 10485760) {
        sizeError = true;
      }

      formData.append("logo", this.croppedBlob);
      formData.append("name", this.tpForm.name);
      formData.append("_method", 'put');

      if (sizeError) {
        self.error = self.$t('system.file_size')
      } else {
        this.buttonDisabled = true;
        piincHttp.post('vendors/' + self.thirdParty.slug, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function(response) {
          self.hideEditTPModal();
          self.buttonDisabled = false;
          // If slug is not changed, just reload content
          if (response.data.slug === self.thirdParty.slug) {
            // Remove last item in breadcrumbs, because it will be added again on load content
            self.breadcrumbItems.pop();
            self.loadContent();
          } else {
            // If slug changed, reload state with new param
            self.$router.push({ name: 'contracts.third_parties.view', params: { slug: response.data.slug }})
          }
          self.$toastr('success', self.$t('maintenance.third_parties.vendors.update.success'))
        }, function(error) {
          self.buttonDisabled = false;
          self.tpForm.error = error.data.message;
          if (error.data.errors.avatar) {
            self.tpForm.error += ' ' + error.data.errors.avatar[0]
          }
          alert(self.tpForm.error)
        })
      }

    },
    // Table list
    loadContracts(page, perPage) {

      if (this.getStorePerPage) {
        this.selectedPerPage = this.getStorePerPage;
      }

      let pageNumber = page ? page : 1;
      let perPageNumber = perPage ? perPage : this.selectedPerPage;
      let self = this;

      piincHttp.get('third-parties', { params:
        {
          page: pageNumber,
          per_page: perPageNumber,
          sort: this.selectedSort !== '' ? this.selectedSort : undefined,
          order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
          vendor: this.thirdParty.id,
          keyword: this.keywordValue !== '' ? this.keywordValue : undefined,
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.tableItems = response.data.items;
        self.loading = false;
        // Set number of total items
        self.totalRows = response.data.total_count;
      }, function() {});

    },
    // Table list
    loadProcessors(page, perPage) {

      this.vendorsLoading = true;

      if (this.getStorePerPage) {
        this.selectedPerPage = this.getStorePerPage;
      }

      let pageNumber = page ? page : 1;
      let perPageNumber = perPage ? perPage : this.selectedPerPage;
      let self = this;

      piincHttp.get('vendors/' + this.$route.params.slug + '/' + 'subdata-processors', { params:
        {
          page: pageNumber,
          per_page: perPageNumber,
          sort: this.selectedSort !== '' ? this.selectedSort : undefined,
          order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
          keyword: this.keywordValue !== '' ? this.keywordValue : undefined,
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.tableProcessorItems = response.data.items;
        self.vendorsLoading = false;
        // Set number of total items
        self.totalProcessorRows = response.data.total_count;
      }, function() {});

    },
    // Table list
    loadProcessorsContracts(page, perPage) {

      if (this.getStorePerPage) {
        this.selectedPerPage = this.getStorePerPage;
      }

      let pageNumber = page ? page : 1;
      let perPageNumber = perPage ? perPage : this.selectedPerPage;
      let self = this;

      piincHttp.get('vendors/' + this.$route.params.slug + '/' + 'subdata-processors-contracts', { params:
                {
                  page: pageNumber,
                  per_page: perPageNumber,
                  sort: this.selectedSort !== '' ? this.selectedSort : undefined,
                  order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
                  keyword: this.keywordValue !== '' ? this.keywordValue : undefined,
                  client: this.getClient.slug
                }
      }).then(function(response) {
        self.tableProcessorContractsItems = response.data.items;
        self.loading = false;
        // Set number of total items
        self.totalProcessorContractsRows = response.data.total_count;
      }, function() {});

    },
    loadCountries() {
      let self = this;
      piincHttp.get('countries-with-flag').then(function(response) {
        self.countries = response.data
      }, function() {});
    },
    loadContent() {
      let self = this;
      piincHttp.get('vendors/' + this.$route.params.slug, { params:
        {
          client: this.getClient.slug,
        }
      }).then(function(response) {
        self.thirdParty = response.data;
        self.loadTasks('update_basic_info');
        self.loadContracts();
        self.loadProcessors();
        self.loadProcessorsContracts();
        // Push Third party name in breadcrumb array
        self.breadcrumbItems.push({text: self.thirdParty.name, active: true});
      }, function(error) {
        if (error.status == 404) {
          self.show404 = true
        }
        if (error.status == 403) {
          self.show403 = true
        }
      });
    },
    loadTasks(code) {
      let self = this;
      if (this.getClient.projectModule) {
        piincHttp.get('projects/me/tasks', { params:
          {
            per_page: 1,
            status: 'pending',
            model_type: this.thirdParty.who_am_i,
            model_id: this.thirdParty.id,
            code: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myTasks.push(response.data.items[0])
          }
        }, function() {});
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('storeTabIndex', '');
    next()
  },
  created () {
    let self = this;
    if (this.getClient && this.getClient.slug) {
      this.loadContent()
    }

    this.loadCountries()

    this.loadGuides('update_basic_info');
    this.loadGuides('update_description');

    // An edit link to a contract opens the contract's edit dialog, not the third party's
    if (this.edit && !this.$route.query.contract) {
      setTimeout(function(){
        self.$refs.editPanel && self.$refs.editPanel.show()
      }, 500);
    }

  }
}
</script>
